import type { Lang } from '../i18n'

export type Phase = 'inhale' | 'hold' | 'exhale' | 'holdOut'

export interface BreathPattern {
  id: string
  /** Seconds per phase. A zero-length phase is skipped entirely. */
  inhale: number
  hold: number
  exhale: number
  holdOut: number
  rounds: number
  label: Record<Lang, string>
  note: Record<Lang, string>
}

/**
 * Every pattern here exhales for longer than it inhales.
 *
 * That asymmetry is the whole mechanism: a long exhale loads the vagus nerve and
 * pulls heart rate down. Patterns with equal or longer inhales feel calming but
 * do much less, and in withdrawal the body needs the actual physiology, not the
 * feeling of doing something.
 */
export const BREATH_PATTERNS: BreathPattern[] = [
  {
    id: 'sigh',
    inhale: 2,
    hold: 1,
    exhale: 6,
    holdOut: 0,
    rounds: 12,
    label: { en: 'Physiological sigh', fa: 'آه فیزیولوژیک' },
    note: {
      en: 'Two breaths in, one long breath out. The fastest way down when the panic is already here.',
      fa: 'دو دم پشت هم، یک بازدم بلند. سریع‌ترین راه پایین آمدن وقتی وحشت همین حالا هست.',
    },
  },
  {
    id: '478',
    inhale: 4,
    hold: 7,
    exhale: 8,
    holdOut: 0,
    rounds: 8,
    label: { en: '4 · 7 · 8', fa: '۴ · ۷ · ۸' },
    note: {
      en: 'The classic. Long hold, longer release. Do not force the hold — shorten it if you strain.',
      fa: 'کلاسیک. نگه‌داشتن طولانی، رهاکردن طولانی‌تر. به زور نگه ندار — اگر فشار آمد کوتاهش کن.',
    },
  },
  {
    id: 'box',
    inhale: 4,
    hold: 4,
    exhale: 4,
    holdOut: 4,
    rounds: 10,
    label: { en: 'Box', fa: 'مربع' },
    note: {
      en: 'Four equal sides. Best when the mind is racing and needs a shape to hold onto.',
      fa: 'چهار ضلع برابر. وقتی ذهن تند می‌رود و به شکلی برای چنگ زدن نیاز دارد.',
    },
  },
  {
    id: 'coherent',
    inhale: 5,
    hold: 0,
    exhale: 6,
    holdOut: 0,
    rounds: 20,
    label: { en: 'Coherent', fa: 'هماهنگ' },
    note: {
      en: 'Almost no effort. Five and a half breaths a minute, for a long time. The one to fall asleep on.',
      fa: 'تقریباً بدون تلاش. پنج‌ونیم نفس در دقیقه، برای مدتی طولانی. همانی که می‌شود رویش خوابید.',
    },
  },
  {
    id: 'long',
    inhale: 4,
    hold: 0,
    exhale: 8,
    holdOut: 2,
    rounds: 12,
    label: { en: 'Double exhale', fa: 'بازدم دوبرابر' },
    note: {
      en: 'Out for twice as long as in. Strong effect, slightly harder to sustain.',
      fa: 'بازدم دو برابر دم. اثر قوی، کمی سخت‌تر برای ادامه دادن.',
    },
  },
]

export function patternById(id: string): BreathPattern {
  return BREATH_PATTERNS.find((p) => p.id === id) ?? BREATH_PATTERNS[0]!
}

/** Ordered phases with their durations, skipping zero-length ones. */
export function phaseSequence(p: BreathPattern): Array<{ phase: Phase; seconds: number }> {
  return (
    [
      { phase: 'inhale' as const, seconds: p.inhale },
      { phase: 'hold' as const, seconds: p.hold },
      { phase: 'exhale' as const, seconds: p.exhale },
      { phase: 'holdOut' as const, seconds: p.holdOut },
    ] satisfies Array<{ phase: Phase; seconds: number }>
  ).filter((s) => s.seconds > 0)
}
